import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Space, Tooltip, Button } from 'antd';
import { EditOutlined, DeleteOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import {
  updateConfigConditions,
  changeConfigContainerVisibility,
} from '@store/errorLogicConfig/configContainer/actions';
import { setWarningContainerVisibility } from '@store/errorLogicConfig/base/actions';
import { normalizeBusinessErrorState } from '../ConfigContainer/utils';

const configStateCode = {
  enable: 2,
  disable: 1,
  delete: 0,
} as const;

function ConfigRowActions({ rowData }) {
  const dispatch = useDispatch();

  const runAction = useCallback(
    (selectedMenuItem: string) => {
      const state =
        selectedMenuItem in configStateCode
          ? configStateCode[selectedMenuItem as keyof typeof configStateCode]
          : normalizeBusinessErrorState(rowData.state);
      dispatch(
        updateConfigConditions({
          id: rowData.id,
          name: rowData.name,
          rules: rowData.rawRules,
          state,
        }),
      );
      if (selectedMenuItem === 'edit') {
        dispatch(changeConfigContainerVisibility(true));
      } else {
        dispatch(setWarningContainerVisibility(true));
      }
    },
    [dispatch, rowData],
  );

  const { parentId = null } = rowData;
  if (parentId !== null) {
    return null;
  }

  const normalizedState = normalizeBusinessErrorState(rowData.state);
  const showEnable = normalizedState === 1;
  const showDisable = normalizedState === 2;

  const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Space size={4} onClick={stop} onMouseDown={stop}>
      <Tooltip title="Edit">
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          aria-label="Edit configuration"
          onClick={() => runAction('edit')}
        />
      </Tooltip>
      {showEnable ? (
        <Tooltip title="Enable">
          <Button
            type="text"
            size="small"
            icon={<CheckCircleOutlined style={{ color: '#389e0d' }} />}
            aria-label="Enable configuration"
            onClick={() => runAction('enable')}
          />
        </Tooltip>
      ) : null}
      {showDisable ? (
        <Tooltip title="Disable">
          <Button
            type="text"
            size="small"
            icon={<StopOutlined style={{ color: '#cf1322' }} />}
            aria-label="Disable configuration"
            onClick={() => runAction('disable')}
          />
        </Tooltip>
      ) : null}
      <Tooltip title="Delete">
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          aria-label="Delete configuration"
          onClick={() => runAction('delete')}
        />
      </Tooltip>
    </Space>
  );
}

export default ConfigRowActions;
